import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';

/**
 * Single communication detail — fetches the full document for one
 * communications row (GET /api/communications/{comm_id}).
 *
 * #524 — used by ContactDetail for fetch-on-expand: the contact timeline
 * returns a bounded plain-text preview only (body/body_html/body_text never
 * reach the list payload), so expanding a communication-source email row
 * fetches the full document to render the sanitised body_html.
 *
 * Mirrors useGmailEmailDetail (useInbox.js) — the repo's fetch-on-open
 * convention: enabled-gated useQuery so the request fires only while the row
 * is expanded (commId is null when collapsed), 5-minute staleTime so
 * re-expanding the same row doesn't refire.
 *
 * @param {string|null} commId - The communication's comm_id (the timeline
 *   row's `id`), or null when no email row is expanded.
 */
export function useCommunicationDetail(commId) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['communication', commId],
        queryFn: async () => {
            const data = await apiFetch(`/communications/${commId}`, getAuthHeader());
            return data;
        },
        enabled: Boolean(commId),
        staleTime: 5 * 60 * 1000,
    });
}
